import * as React from 'react';
import { useForm } from 'react-hook-form';
import { typeboxResolver } from '@hookform/resolvers/typebox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { wallet } from '@b1fr0st/contracts';
import type { Static } from '@sinclair/typebox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/forms/Form';
import { FormMoneyInput, MoneyInput } from '@/components/forms/FormMoneyInput';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { LabelWithHelp } from '@/components/forms/FieldHelp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useListQuery } from '@/hooks/useListQuery';
import { api } from '@/lib/api-client';
import { errorMessage } from '@/lib/errors';
import { moneyHint } from '@/lib/money';
import {
  beyondLimit,
  type AdjustLimitKind,
  type MyApprovalLimit,
  type MyApprovalLimits,
} from './adjust-limits';
import { turnoverForGrant } from './adjust-turnover';
import { toast } from '@/components/ui/toast';

type AdjustValues = Static<typeof wallet.AdminAdjustRequest>;

/** An operator's own bank account, from svc-finance's house-account register. */
interface HouseBankAccount {
  id: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  usage: string;
}

interface ActivePromo {
  id: string;
  name: string;
  code: string;
  kind: string;
  turnoverMultiplier: string;
  expiryDays: number | null;
  conditions: Record<string, unknown> | null;
}

/**
 * Campaigns a manual bonus grant may NOT be attached to.
 *
 * These pay into the CASHBACK pool from their own period accrual, and their
 * `turnover_multiplier` is a term on those credits — legitimately 0.00 now, and
 * the default for a new one. Attaching a bonus grant to one would mint
 * promotion-pool principal with no wagering requirement at all: withdrawable
 * the moment it lands. svc-promo refuses it (PROMO-068); they are dropped from
 * the list here so the operator never picks one and meets that refusal after
 * typing the amount.
 */
const GRANT_EXCLUDED_KINDS = new Set(['cashback', 'turnover_rebate']);

const PROMO_NONE = '__none__';
/**
 * How long an unplayed bonus lock stands before the expiry sweeper forfeits it
 * and the member's withdrawals unblock.
 *
 * Mirrors `ADMIN_BONUS_EXPIRY_DAYS` in `apps/promo/src/repo/admin-bonus.ts`,
 * which is the value that actually governs. Duplicated rather than fetched
 * because this screen has no config endpoint to ask and the number has to be on
 * the form BEFORE the operator commits; change both together.
 */
export const BONUS_EXPIRY_DAYS = 30;

const BANK_NONE = 'none';

const LIMITS_RESOURCE = '/api/v1/approval-limits/me';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
  /**
   * The MEMBER's wallet currency — both call sites already run the wallet query
   * that carries it, and both gate the button that opens this dialog on the same
   * `wallet:read` permission that enables the query, so `undefined` here always
   * means "the request has not answered yet", never "this operator will never
   * learn it".
   *
   * `undefined` is NOT a currency and must never be substituted by one. A credit
   * entered under the wrong exponent is a 100x error nothing downstream can
   * detect: both sides agree on the integer, and the scale was lost at the
   * boundary (BO-05). This is the console's most money-critical input — the
   * figure moves a real balance — so the unresolved state renders as ABSENCE:
   * no money box at all, an explanation in its place, and Save disabled. See
   * `CouponForm` and `FinanceConfigDialog` for the same treatment on
   * lower-stakes screens.
   */
  currency: string | undefined;
}

export function AdjustDialog({ open, onOpenChange, memberId, currency }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canAttachPromo = hasPermission('promotion:approve');
  const canReadLimits = hasPermission('approval_limit:read');
  const [bankId, setBankId] = React.useState(BANK_NONE);
  const [promotionId, setPromotionId] = React.useState('');
  const [turnover, setTurnover] = React.useState(0);
  const form = useForm<AdjustValues>({
    resolver: typeboxResolver(wallet.AdminAdjustRequest),
    defaultValues: {
      memberId,
      pool: 'cash',
      op: 'add',
      amount: 0,
      reason: '',
    },
  });
  /**
   * The one place `currency` is read, normalised to a single unresolved value.
   *
   * An empty string is folded into `undefined` deliberately: `decimalsFor('')`
   * falls through to 2, so a blank code coming off the wire would be an
   * invisible THB assumption on a JPY member — the exact BO-05 shape, wearing a
   * value the type system calls a currency. Nothing below may reach for
   * `currency` again; this is what the boxes and the submit guard both ask.
   */
  const walletCurrency = currency !== undefined && currency.trim() !== '' ? currency : undefined;
  const pool = form.watch('pool');
  const op = form.watch('op');
  const amount = form.watch('amount');
  // Required by the SERVICE too (WALLET-024). Blocking Save here only saves the
  // operator a round trip; the rule lives where a script cannot skip it, which
  // is the same arrangement `ReviewRemarkDialog` keeps for FIN-332.
  const reasonMissing = (form.watch('reason') ?? '').trim() === '';
  const showPromo = pool === 'promotion' && canAttachPromo;

  React.useEffect(() => {
    if (open) {
      form.reset({ memberId, pool: 'cash', op: 'add', amount: 0, reason: '' });
      setBankId(BANK_NONE);
      setPromotionId('');
      setTurnover(0);
    }
  }, [open, memberId, form]);

  // The account a manual cash movement is settled against is the OPERATOR's, not
  // the member's: crediting cash by hand means the house received the money,
  // debiting it means the house paid it out. The member's own accounts answer
  // neither question, and offering them let an operator record a bank statement
  // line that no house account could ever be reconciled to.
  const { data: houseBanks } = useListQuery<HouseBankAccount>(
    open && pool === 'cash' ? '/api/v1/operator/banks/bo' : '',
    { pageSize: 100, isActive: 'true' },
  );
  // An account flagged for the other direction cannot have moved this money.
  const bankOptions = (houseBanks?.items ?? []).filter((b) =>
    op === 'sub'
      ? b.usage === 'withdraw' || b.usage === 'both'
      : b.usage === 'deposit' || b.usage === 'both',
  );
  // Flipping add→sub can strip the chosen account off the list. Radix then has
  // no item to name and renders the trigger blank while the submit still carries
  // the id, so fall back to the placeholder and make the operator choose again.
  const selectedBankId = bankOptions.some((b) => b.id === bankId) ? bankId : BANK_NONE;

  const { data: promos } = useListQuery<ActivePromo>(
    canAttachPromo ? '/api/v1/promotions' : '',
    { pageSize: 200, status: 'active' },
  );
  const grantablePromos = (promos?.items ?? []).filter((p) => !GRANT_EXCLUDED_KINDS.has(p.kind));
  const selectedPromo = grantablePromos.find((p) => p.id === promotionId);

  // Pre-fill turnover from bonus × the promotion's multiplier; editable after.
  React.useEffect(() => {
    if (!promotionId || !selectedPromo) return;
    setTurnover(turnoverForGrant(Number(amount) || 0, selectedPromo.turnoverMultiplier));
  }, [promotionId, selectedPromo, amount]);

  // Subtracting a turnover-locked grant is meaningless — force 'add'.
  React.useEffect(() => {
    if (showPromo && promotionId) form.setValue('op', 'add');
  }, [showPromo, promotionId, form]);

  const promoAttached = showPromo && !!promotionId;

  /**
   * The operator's OWN ceilings, in the currency this adjustment is denominated
   * in — asked for explicitly, because a limit is stored per currency and the
   * platform default is not this member's brand (CLAUDE.md §2.7).
   *
   * Withheld until the wallet currency resolves, for the same reason the amount
   * box is: a ceiling rendered under a guessed exponent reads 100x wrong on the
   * one line whose job is telling the operator what they may do.
   */
  const limits = useQuery<MyApprovalLimits>({
    queryKey: [LIMITS_RESOURCE, walletCurrency],
    queryFn: () => api.get<MyApprovalLimits>(`${LIMITS_RESOURCE}?currency=${walletCurrency}`),
    enabled: open && canReadLimits && walletCurrency !== undefined,
    retry: false,
  });

  const limitFor = (kind: AdjustLimitKind): MyApprovalLimit | undefined =>
    limits.data?.items.find((item) => item.kind === kind);

  const limitCurrency = limits.data?.currency ?? walletCurrency;

  const limitLabel = (kind: AdjustLimitKind): string => {
    const limit = limitFor(kind);
    if (!limit) return '—';
    if (limit.unlimited) return t('wallet.limits.unlimited');
    if (limit.source === 'none' || limit.maxAmount === null) return t('wallet.limits.none');
    const denomination = limit.currency || limitCurrency;
    // Absence shown as absence. Falling through to `moneyHint`'s own 'THB'
    // default is the exact substitution this screen already withholds the amount
    // box to avoid: a ceiling rendered under a guessed exponent is 100x wrong.
    if (!denomination) return '—';
    // String in, string out — `moneyHint` never turns minor units into a float.
    return moneyHint(limit.maxAmount, denomination);
  };

  // A promotion claim is a different write on a different route, and it is
  // held to the same `adjust_add` ceiling anyway: the operator is handing
  // the player value from this dialog and this button, and svc-promo refuses it
  // with PROMO-115 the way svc-wallet refuses a direct top-up with WALLET-023.
  // Excluding it here was how the dialog told an operator with a ฿1,000 ceiling
  // that Save was available for a ฿50,000 bonus.
  //
  // `adjust_add` unconditionally when a promotion is attached, never the current
  // direction: attaching one forces `op` to 'add' (the effect below), so reading
  // the deduction ceiling here would be reading the wrong row during the render
  // in which that has not happened yet.
  const overLimit = beyondLimit(
    Number(amount) || 0,
    limitFor(promoAttached || op !== 'sub' ? 'adjust_add' : 'adjust_sub'),
  );

  const { data: providers } = useListQuery<{ id: string; name: string; displayName?: string }>(
    promoAttached ? '/api/v1/providers' : '',
    { pageSize: 200 },
  );
  const providerName = (id: string) => {
    const p = providers?.items.find((x) => x.id === id);
    return p?.displayName ?? p?.name ?? id;
  };

  const cond = (selectedPromo?.conditions ?? {}) as Record<string, unknown>;
  const asArr = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);
  const gameTypes = asArr(cond.allowedGameTypes);
  const provIds = asArr(cond.allowedProviders);

  // svc-wallet dedups an adjustment on the key the caller sends, and only on
  // that: the key it mints for a caller that sends none is fresh every request,
  // by design. So the guard against a double credit lives here.
  //
  // The key is tied to the FIGURES, not to the dialog and not to the click. The
  // vector is an operator who saw a timeout or an error for a request svc-wallet
  // had in fact committed, and pressed Save again on the same numbers — that
  // repeat lands on the same key and collapses into the original posting. Change
  // any of the numbers and it is a different adjustment that must land on its
  // own, so it gets its own key.
  const keyRef = React.useRef<{ signature: string; key: string } | null>(null);
  const idempotencyKeyFor = (signature: string) => {
    if (keyRef.current?.signature !== signature) {
      keyRef.current = { signature, key: `bo:adjust:${crypto.randomUUID()}` };
    }
    return keyRef.current.key;
  };

  React.useEffect(() => {
    if (open) keyRef.current = null;
  }, [open]);

  const mutation = useMutation({
    mutationFn: (values: AdjustValues) => {
      const reason = values.reason?.trim();
      if (values.pool === 'promotion' && promotionId) {
        return api.post('/api/v1/promotion-claims', {
          promotionId,
          memberId,
          amount: values.amount,
          turnover,
          reason,
        });
      }
      const houseBankAccountId =
        values.pool === 'cash' && selectedBankId !== BANK_NONE ? selectedBankId : undefined;
      const turnoverRequired =
        values.pool === 'promotion' && values.op === 'add' && turnover > 0 ? turnover : undefined;
      return api.post(`/api/v1/wallets/${memberId}/adjust`, {
        ...values,
        reason,
        houseBankAccountId,
        turnoverRequired,
        idempotencyKey: idempotencyKeyFor(
          JSON.stringify([
            memberId,
            values.pool,
            values.op,
            values.amount,
            reason ?? null,
            houseBankAccountId ?? null,
            turnoverRequired ?? null,
          ]),
        ),
      });
    },
    onSuccess: () => {
      toast.success(t('wallet.adjustSuccess'));
      qc.invalidateQueries({ queryKey: [`/api/v1/wallets/${memberId}`] });
      qc.invalidateQueries({ queryKey: [`/api/v1/wallets/${memberId}/transactions`] });
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err, t('errors.generic'))),
  });

  const onSubmit = form.handleSubmit((values) => {
    // Save is disabled and the amount box is not even mounted, so this is the
    // third lock rather than the first. It is here because neither of the other
    // two is a property of the CODE: a `requestSubmit()` from anywhere reaches
    // this handler past a disabled button, and the figure it would carry is the
    // untouched default rather than anything the operator chose.
    if (walletCurrency === undefined) return;
    // The server refuses this with WALLET-023 regardless; blocking here is so the
    // operator learns it before the money screen tells them, not instead of it.
    if (overLimit) return;
    if (reasonMissing) return;
    mutation.mutate(values);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('wallet.adjust')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FormSelect<AdjustValues>
                name="op"
                label={<LabelWithHelp text={t('wallet.op')} help={t('wallet.help.op')} />}
                disabled={promoAttached}
                options={[
                  { value: 'add', label: t('wallet.opAdd') },
                  { value: 'sub', label: t('wallet.opSub') },
                ]}
              />
              <FormSelect<AdjustValues>
                name="pool"
                label={<LabelWithHelp text={t('wallet.pool')} help={t('wallet.help.pool')} />}
                options={[
                  { value: 'cash', label: t('wallet.creditCash') },
                  { value: 'promotion', label: t('wallet.creditPromotion') },
                ]}
              />
            </div>
            {/* Both directions, side by side and always both — the operator has
                to know which one applies before choosing add or sub, and a line
                that showed only the current direction would hide the fact that
                they are two separate ceilings. Rendered only once the currency
                is known; an amount without its exponent is not a figure. */}
            {canReadLimits && walletCurrency !== undefined && limits.data && (
              <p id="wallet-adjust-limits" className="text-xs text-muted-foreground">
                {t('wallet.limits.add')}: {limitLabel('adjust_add')} · {t('wallet.limits.sub')}:{' '}
                {limitLabel('adjust_sub')}
              </p>
            )}
            {overLimit && (
              <p
                id="wallet-adjust-over-limit"
                className="text-xs text-amber-600 dark:text-amber-400"
              >
                {t('wallet.limits.over')}
              </p>
            )}
            {showPromo && (
              <div className="space-y-1">
                <LabelWithHelp
                  text={`${t('wallet.promotionCondition')} (${t('common.optional')})`}
                  help={t('wallet.help.promotionCondition')}
                />
                <Select
                  value={promotionId || PROMO_NONE}
                  onValueChange={(v) => setPromotionId(v === PROMO_NONE ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PROMO_NONE}>{t('wallet.promotionConditionNone')}</SelectItem>
                    {grantablePromos.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {promoAttached && (
              <div className="rounded-md border p-3 text-sm space-y-1 text-muted-foreground">
                <p className="font-medium text-foreground">{t('wallet.conditionsTitle')}</p>
                <p>{t('wallet.turnoverScope')}</p>
                <p className="pl-3">
                  {t('wallet.condGames')}:{' '}
                  {gameTypes.length
                    ? gameTypes.map((g) => t(`promotion.gameType.${g}`, g)).join(', ')
                    : t('wallet.allGames')}
                </p>
                <p className="pl-3">
                  {t('wallet.condProviders')}:{' '}
                  {provIds.length ? provIds.map(providerName).join(', ') : t('wallet.allProviders')}
                </p>
                <p>
                  {selectedPromo?.expiryDays
                    ? t('wallet.expiresInDays', { count: selectedPromo.expiryDays })
                    : t('wallet.noExpiry')}
                </p>
              </div>
            )}
            {/*
                * Withheld, not defaulted. `currency ?? ''` used to mount the box
                * regardless: `decimalsFor('')` is 2, so an operator crediting a
                * JPY member typed 2000 and the ledger received 200000 minor
                * units — ¥200,000 — with the correct-looking figure still on
                * screen. Save was already blocked until the currency landed,
                * which stopped the submit but not the typing: the box was live
                * under a guessed exponent and `MoneyInput`'s late-currency path
                * then re-emitted from the VISIBLE TEXT, so what the operator had
                * already entered was re-priced under whatever arrived.
                *
                * Keyed on the denomination for the same reason `CouponForm` is:
                * nothing here is ever mounted under a guess, so a denomination
                * that does change under a MOUNTED box can only mean the stored
                * integer must be re-read at the new exponent — never re-priced
                * from the digits, which would rewrite the form value by 100x.
                *
                * What the key covers is exactly that — the FIGURES' scale. It is
                * not a change of SUBJECT: the drawer swapping to another member
                * of the same currency leaves this key identical and remounts
                * nothing. That case is handled a few lines up instead, by the
                * effect that resets the form on `memberId`, which zeroes the
                * amount; `MoneyInput` then re-seeds its visible text from the
                * value it was given and clears the typed-since-seed mark, so the
                * previous member's digits are gone either way. Deleting that
                * reset on the strength of this key would leave one operator's
                * half-typed figure sitting over another member's wallet.
                */}
            {walletCurrency !== undefined ? (
              <FormMoneyInput<AdjustValues>
                key={walletCurrency}
                name="amount"
                currency={walletCurrency}
                label={<LabelWithHelp text={t('wallet.amount')} help={t('wallet.help.amount')} />}
              />
            ) : (
              <div className="space-y-1">
                <LabelWithHelp text={t('wallet.amount')} help={t('wallet.help.amount')} />
                <p id="wallet-adjust-currency-unknown" className="text-xs text-muted-foreground">
                  {t('wallet.amountCurrencyUnknown')}
                </p>
              </div>
            )}
            {/* Not gated on an attached promotion: a bonus granted without one
                is still bonus principal, and leaving the requirement off the
                screen would let it be credited unlocked. The figure is enforced
                either way — with a promotion the grant becomes a claim on that
                campaign, without one svc-wallet locks it against a claim of its
                own before the credit posts. */}
            {pool === 'promotion' && op === 'add' && (
              <div className="space-y-1">
                <LabelWithHelp
                  text={t('wallet.turnoverRequired')}
                  help={t('wallet.help.turnoverRequired')}
                />
                {walletCurrency !== undefined ? (
                  <MoneyInput
                    key={walletCurrency}
                    value={turnover}
                    currency={walletCurrency}
                    onChange={(minor) => setTurnover(minor === '' ? 0 : Number(minor))}
                  />
                ) : (
                  <p
                    id="wallet-adjust-turnover-currency-unknown"
                    className="text-xs text-muted-foreground"
                  >
                    {t('wallet.amountCurrencyUnknown')}
                  </p>
                )}
                {/* Two consequences the operator cannot see anywhere else and is
                    committing the member to. The withdrawal gate is
                    all-or-nothing: while ANY wagering is outstanding the member
                    cannot withdraw at all, so a small goodwill bonus with a
                    large requirement freezes a large balance. And the lock is
                    not forever — it expires, which is the only thing that
                    unfreezes them without an operator acting. Neither fact is on
                    this screen otherwise, and neither is in the refusal the
                    player gets. Visible, not a tooltip. */}
                {turnover > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('wallet.turnoverBlocksWithdrawal', { days: BONUS_EXPIRY_DAYS })}
                  </p>
                )}
              </div>
            )}
            {pool === 'cash' && (
              <div className="space-y-1">
                <LabelWithHelp
                  text={`${t('wallet.bankAccount')} (${t('common.optional')})`}
                  help={t('wallet.help.bankAccount')}
                />
                <Select value={selectedBankId} onValueChange={setBankId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={BANK_NONE}>—</SelectItem>
                    {bankOptions.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {[b.bankCode, b.accountNumber, b.accountName].filter(Boolean).join(' · ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <FormTextarea<AdjustValues>
                name="reason"
                label={<LabelWithHelp text={t('finance.reason')} help={t('wallet.help.reason')} />}
                rows={3}
                aria-required
              />
              {reasonMissing && (
                <p id="wallet-adjust-reason-required" role="alert" className="text-xs text-destructive">
                  {t('wallet.reasonRequired')}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              {/* Blocked until the member's currency is known: an amount typed
                  under a guessed exponent is a silent 100x error. The box is
                  withheld too, so this is the second lock rather than the only
                  one — it also covers a blank code, which the box folds into
                  "unresolved" and this used to accept as a currency. */}
              <Button
                type="submit"
                disabled={
                  mutation.isPending || walletCurrency === undefined || overLimit || reasonMissing
                }
              >
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
